/**
 * "Crop from paper" — the editor's side of re-cutting a question image from the
 * paper the question was extracted from.
 *
 * The form owns the source (loaded once per question) and the crop modal; a
 * field only knows its slot and asks the context to open the paper for it. The
 * result lands in form state — url, hash and source region together — and is
 * saved by the form's Save like everything else.
 *
 * See docs/FeatureDesigns/question-image-recrop-target.md.
 */
import { createContext, useContext } from 'react';
import {
    imageSlotKey, upsertSourceRegion,
    type PageRect, type QuestionImageSlot, type QuestionSourceRegion,
} from '@padcanvas/shared';
import type { QuestionFormData } from './formTypes';

export interface QuestionSourcePage {
    /** The page's real number in the document. */
    page: number;
    /** Upright whole-page image; its fractions are a region's fractions. */
    url: string;
    /** False when the page's import edits are not on record — cutting it could
     *  bring back what a mask hid. */
    croppable: boolean;
}

export type QuestionSourceInfo =
    | {
        available: true;
        questionSetId: string;
        sourceKind: 'pdf' | 'images';
        pages: QuestionSourcePage[];
        regions: QuestionSourceRegion[];
    }
    | { available: false; reason: 'not-extracted' | 'docx' | 'swept' | 'no-pages' };

export interface QuestionSourceCropResult {
    url: string;
    hash: string;
    region: QuestionSourceRegion;
}

/** Supplied by the host app — the endpoints differ only by fetch wrapper. */
export interface QuestionSourceApi {
    getSource: (questionId: string) => Promise<QuestionSourceInfo>;
    cropFromSource: (questionId: string, body: {
        slot: QuestionImageSlot; questionSetId: string; page: number; rect: PageRect;
    }) => Promise<QuestionSourceCropResult>;
}

export interface QuestionSourceContextValue {
    /** The paper can be opened for this question. */
    available: boolean;
    openCrop: (slot: QuestionImageSlot) => void;
}

export const QuestionSourceContext = createContext<QuestionSourceContextValue | null>(null);

/** Null outside a form that was given a source API, so fields render nothing. */
export function useQuestionSource(): QuestionSourceContextValue | null {
    const ctx = useContext(QuestionSourceContext);
    return ctx?.available ? ctx : null;
}

const sameLabel = (a: string | undefined, b: string) => (a ?? '').toLowerCase() === b.toLowerCase();

/**
 * Put a cropped image into its slot: the URL, the hash of those exact bytes,
 * and the region it was cut from. Pure, so the slot addressing is testable.
 * A slot whose option or part is not in the form is left alone.
 */
export function applySlotImage(form: QuestionFormData, result: QuestionSourceCropResult): QuestionFormData {
    const { url, hash, region } = result;
    const slot = region.slot;
    const next: QuestionFormData = { ...form, sourceRegions: upsertSourceRegion(form.sourceRegions, region) };
    switch (slot.kind) {
        case 'questionDiagram':
            return { ...next, questionDiagramUrl: url, questionDiagramHash: hash };
        case 'solutionImage':
            return { ...next, solutionImageUrl: url, solutionImageHash: hash };
        case 'optionImage': {
            if (!form.options?.some(o => sameLabel(o.label, slot.option))) return form;
            return {
                ...next,
                options: form.options.map(o => sameLabel(o.label, slot.option) ? { ...o, imageUrl: url, imageHash: hash } : o),
            };
        }
        case 'partDiagram':
        case 'partOptionImage': {
            if (!form.parts?.some(p => p.partId === slot.partId)) return form;
            return {
                ...next,
                parts: form.parts.map(p => {
                    if (p.partId !== slot.partId) return p;
                    if (slot.kind === 'partDiagram') return { ...p, diagramUrl: url, diagramHash: hash };
                    return {
                        ...p,
                        options: (p.options ?? []).map(o => sameLabel(o.label, slot.option) ? { ...o, imageUrl: url, imageHash: hash } : o),
                    };
                }),
            };
        }
        case 'questionFigure': {
            if (!form.figures?.some(f => f.id === slot.figureId)) return form;
            return {
                ...next,
                figures: form.figures.map(f => (f.id === slot.figureId ? { ...f, url, hash } : f)),
            };
        }
        case 'partFigure': {
            const part = form.parts?.find(p => p.partId === slot.partId);
            if (!part?.figures?.some(f => f.id === slot.figureId)) return form;
            return {
                ...next,
                parts: form.parts!.map(p => (p.partId !== slot.partId ? p : {
                    ...p,
                    figures: (p.figures ?? []).map(f => (f.id === slot.figureId ? { ...f, url, hash } : f)),
                })),
            };
        }
    }
}

/** The region on record for a slot, if its image came from the paper. */
export function regionForSlot(
    regions: readonly QuestionSourceRegion[] | undefined,
    slot: QuestionImageSlot,
): QuestionSourceRegion | undefined {
    const key = imageSlotKey(slot);
    return regions?.find(r => imageSlotKey(r.slot) === key);
}
